import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Description, CloudUpload, SmartToy, Delete } from '@mui/icons-material';
import { BRAND_COLORS } from '../../../styles/theme';
import { useTranslation } from '../../../i18n';
import { formatDateISO } from '../../../utils/dateUtils';
import type {
  AdminDialogProps,
  MaintenanceDocumentListResponse,
  MaintenanceDocumentMetadata,
  MaintenanceDocumentUploadResponse,
} from '../types/adminTools.types';

interface MaintenanceDocsDialogProps extends AdminDialogProps {
  loading: boolean;
  docsData: MaintenanceDocumentListResponse | null;
  error: string;
  selectedFile: File | null;
  documentTitle: string;
  documentType: string;
  documentVersion: string;
  droneModelFilter: string;
  manufacturerFilter: string;
  uploading: boolean;
  uploadResult: MaintenanceDocumentUploadResponse | null;
  uploadError: string;
  deletingDocKey: string | null;
  processingDocKey: string | null;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => Promise<void>;
  onDelete: (s3Key: string) => Promise<void>;
  onProcess: (s3Key: string) => Promise<void>;
  onDocumentTitleChange: (value: string) => void;
  onDocumentTypeChange: (value: string) => void;
  onDocumentVersionChange: (value: string) => void;
  onDroneModelFilterChange: (value: string) => void;
  onManufacturerFilterChange: (value: string) => void;
}

export const MaintenanceDocsDialog: React.FC<MaintenanceDocsDialogProps> = ({
  open,
  onClose,
  loading,
  docsData,
  error,
  selectedFile,
  documentTitle,
  documentType,
  documentVersion,
  droneModelFilter,
  manufacturerFilter,
  uploading,
  uploadResult,
  uploadError,
  deletingDocKey,
  processingDocKey,
  onFileSelect,
  onUpload,
  onDelete,
  onProcess,
  onDocumentTitleChange,
  onDocumentTypeChange,
  onDocumentVersionChange,
  onDroneModelFilterChange,
  onManufacturerFilterChange,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth data-testid="maintenance-docs-dialog">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description />
          {t('adminToolsPage.maintenanceDocs.dialogTitle')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Upload Section */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <CloudUpload />
              {t('adminToolsPage.maintenanceDocs.uploadSection')}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <input
                accept=".pdf"
                style={{ display: 'none' }}
                id="maintenance-doc-file-input"
                type="file"
                onChange={onFileSelect}
                disabled={uploading}
              />
              <label htmlFor="maintenance-doc-file-input">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                  sx={{ mb: 2 }}
                >
                  {t('adminToolsPage.maintenanceDocs.selectPdfFile')}
                </Button>
              </label>

              {selectedFile && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t('adminToolsPage.maintenanceDocs.selectedFile', {
                    name: selectedFile.name,
                    size: (selectedFile.size / (1024 * 1024)).toFixed(2),
                  })}
                </Typography>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('adminToolsPage.maintenanceDocs.documentTitle')}
                    value={documentTitle}
                    onChange={(e) => onDocumentTitleChange(e.target.value)}
                    disabled={uploading}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('adminToolsPage.maintenanceDocs.documentType')}</InputLabel>
                    <Select
                      value={documentType}
                      onChange={(e) => onDocumentTypeChange(e.target.value)}
                      label={t('adminToolsPage.maintenanceDocs.documentType')}
                      disabled={uploading}
                    >
                      <MenuItem value="manual">
                        {t('adminToolsPage.maintenanceDocs.docTypes.manual')}
                      </MenuItem>
                      <MenuItem value="procedure">
                        {t('adminToolsPage.maintenanceDocs.docTypes.procedure')}
                      </MenuItem>
                      <MenuItem value="tool_spec">
                        {t('adminToolsPage.maintenanceDocs.docTypes.toolSpec')}
                      </MenuItem>
                      <MenuItem value="diagram">
                        {t('adminToolsPage.maintenanceDocs.docTypes.diagram')}
                      </MenuItem>
                      <MenuItem value="safety">
                        {t('adminToolsPage.maintenanceDocs.docTypes.safety')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('adminToolsPage.maintenanceDocs.version')}
                    value={documentVersion}
                    onChange={(e) => onDocumentVersionChange(e.target.value)}
                    disabled={uploading}
                    size="small"
                    placeholder={t('adminToolsPage.maintenanceDocs.versionPlaceholder')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('adminToolsPage.maintenanceDocs.droneModel')}
                    value={droneModelFilter}
                    onChange={(e) => onDroneModelFilterChange(e.target.value)}
                    disabled={uploading}
                    size="small"
                    placeholder={t('adminToolsPage.maintenanceDocs.droneModelPlaceholder')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('adminToolsPage.maintenanceDocs.manufacturer')}
                    value={manufacturerFilter}
                    onChange={(e) => onManufacturerFilterChange(e.target.value)}
                    disabled={uploading}
                    size="small"
                    placeholder={t('adminToolsPage.maintenanceDocs.manufacturerPlaceholder')}
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                onClick={onUpload}
                disabled={uploading || !selectedFile || !documentTitle.trim()}
                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                sx={{
                  mt: 2,
                  backgroundColor: BRAND_COLORS.forestGreen.main,
                  '&:hover': {
                    backgroundColor: BRAND_COLORS.forestGreen.main,
                    opacity: 0.8,
                  },
                }}
              >
                {uploading
                  ? t('adminToolsPage.maintenanceDocs.uploading')
                  : t('adminToolsPage.maintenanceDocs.uploadDocument')}
              </Button>

              {uploadResult && (
                <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                  {uploadResult.message || uploadResult.error}
                </Alert>
              )}

              {uploadError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {uploadError}
                </Alert>
              )}
            </Box>
          </Paper>

          {/* Documents List Section */}
          <Typography variant="h6" gutterBottom>
            {t('adminToolsPage.maintenanceDocs.uploadedDocuments')}
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : docsData ? (
            <Box>
              {docsData.documents.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  {t('adminToolsPage.maintenanceDocs.noDocsYet')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t('adminToolsPage.maintenanceDocs.tableHeaders.title')}
                        </TableCell>
                        <TableCell>
                          {t('adminToolsPage.maintenanceDocs.tableHeaders.type')}
                        </TableCell>
                        <TableCell>
                          {t('adminToolsPage.maintenanceDocs.tableHeaders.size')}
                        </TableCell>
                        <TableCell>
                          {t('adminToolsPage.maintenanceDocs.tableHeaders.status')}
                        </TableCell>
                        <TableCell>
                          {t('adminToolsPage.maintenanceDocs.tableHeaders.uploaded')}
                        </TableCell>
                        <TableCell align="center">
                          {t('adminToolsPage.maintenanceDocs.tableHeaders.actions')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {docsData.documents.map((doc: MaintenanceDocumentMetadata) => (
                        <TableRow key={doc.s3_key}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {doc.document_title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={doc.document_type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{(doc.file_size / (1024 * 1024)).toFixed(2)} MB</TableCell>
                          <TableCell>
                            {doc.is_processed ? (
                              <Chip
                                label={t('adminToolsPage.maintenanceDocs.processed', {
                                  chunks: doc.chunk_count,
                                })}
                                size="small"
                                color="success"
                                icon={<SmartToy />}
                              />
                            ) : (
                              <Chip
                                label={t('adminToolsPage.maintenanceDocs.notProcessed')}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>{formatDateISO(doc.uploaded_at)}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip
                                title={
                                  doc.is_processed
                                    ? t('adminToolsPage.maintenanceDocs.reprocessDoc')
                                    : t('adminToolsPage.maintenanceDocs.processForAI')
                                }
                              >
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onProcess(doc.s3_key)}
                                  disabled={processingDocKey === doc.s3_key}
                                >
                                  {processingDocKey === doc.s3_key ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <SmartToy />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('adminToolsPage.maintenanceDocs.deleteDocument')}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onDelete(doc.s3_key)}
                                  disabled={
                                    deletingDocKey === doc.s3_key || processingDocKey === doc.s3_key
                                  }
                                >
                                  {deletingDocKey === doc.s3_key ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Delete />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminToolsPage.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
